import {
	Anchor,
	Avatar,
	Chip,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@exopengithub/exokit";
import { clsx } from "clsx";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { COMPANYADMIN } from "src/common/service/users.js";
import type { User } from "src/common/service/types/UserTypes.js";
import { getGravatarUrl } from "../planning/utils/getGravatarUrl.js";
import { getGroupName } from "./getGroupName.js";

/**
 * The group's own colour, falling back to purple when it has none.
 * WHY clsx: the two hand-written copies of this interpolated the literal
 * string "undefined" into className whenever a colour *was* set.
 */
export const GroupColorDot = ({ colorCode }: { colorCode?: string | null }) => (
	<div
		className={clsx(
			"h-[20px] w-[20px] rounded-full",
			!colorCode && "bg-purple-500",
		)}
		style={colorCode ? { backgroundColor: colorCode } : undefined}
	/>
);

/** A user's groups in this domain, named and ordered the way they read. */
export const userAccessGroups = (user: User, companyId: string, t: TFunction) =>
	user.accessGroups
		.filter((group) => group.companyId === Number(companyId))
		.toSorted((a, b) =>
			getGroupName({ t, groupName: a.name }).localeCompare(
				getGroupName({ t, groupName: b.name }),
			),
		);

export const UserNameCell = ({
	user,
	onEdit,
}: {
	user: User;
	onEdit: (user: User) => void;
}) => (
	<div className="flex items-center gap-2">
		<Avatar src={getGravatarUrl(user.email)} size="sm" alt="" />
		<Anchor<"a">
			onClick={(event) => {
				event.preventDefault();
				onEdit(user);
			}}
		>
			{user.name}
		</Anchor>
		{user.superAdmin && (
			<Chip variant="purple" size="small">
				Superadmin
			</Chip>
		)}
	</div>
);

export const UserAccessCell = ({
	user,
	companyId,
}: {
	user: User;
	companyId: string;
}) => {
	const { t } = useTranslation();
	return userAccessGroups(user, companyId, t).some(
		(group) => group.name === COMPANYADMIN,
	)
		? t("CompanyAdmin")
		: t("User");
};

export const UserGroupsCell = ({
	user,
	companyId,
}: {
	user: User;
	companyId: string;
}) => {
	const { t } = useTranslation();
	return (
		<div className="flex gap-2">
			{userAccessGroups(user, companyId, t).map((group) => (
				<Tooltip key={group.groupId}>
					<TooltipTrigger>
						<GroupColorDot colorCode={group.colorCode} />
					</TooltipTrigger>
					<TooltipContent>
						{getGroupName({ t, groupName: group.name })}
					</TooltipContent>
				</Tooltip>
			))}
		</div>
	);
};
